import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  StyleSheet,
  useWindowDimensions,
  View,
  Animated,
  Easing,
} from 'react-native';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import SecureImage from '../../components/Reusables/SecureImage';
import SafeAreaWrapper from '../../components/Reusables/SafeAreaWrapper';
import Button from '../../components/Reusables/Button';
import Typography from '../../components/Reusables/Typography';
import { WELCOME_WALL_IMAGES } from '../../constants/onboarding';
import { useTheme, GTFonts } from '../../theme';
import { fs, iconSize, s, v } from '../../utils/libs';
import { isTablet } from 'react-native-device-info';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';

type Direction = 'left' | 'right';

type MarqueeRowProps = {
  images: string[];
  direction: Direction;
  durationMs: number;
  itemWidth: number;
  itemHeight: number;
  gap: number;
  borderRadius: number;
};

const MarqueeRow: React.FC<MarqueeRowProps> = React.memo(
  ({
    images,
    direction,
    durationMs,
    itemWidth,
    itemHeight,
    gap,
    borderRadius,
  }) => {
    const contentWidth = useMemo(() => {
      if (!images.length) return 0;
      return images.length * itemWidth + (images.length - 1) * gap;
    }, [gap, images.length, itemWidth]);

    const translateX = useRef(new Animated.Value(0)).current;

    useEffect(() => {
      if (!contentWidth) return;

      translateX.setValue(direction === 'left' ? 0 : -contentWidth);
      const toValue = direction === 'left' ? -contentWidth : 0;

      const anim = Animated.loop(
        Animated.timing(translateX, {
          toValue,
          duration: durationMs,
          easing: Easing.linear,
          //has to be false for web
          useNativeDriver: false,
        }),
        { resetBeforeIteration: true },
      );

      anim.start();
      return () => anim.stop();
    }, [contentWidth, direction, durationMs, translateX]);

    const renderStrip = useCallback(() => {
      return images.map((uri, idx) => (
        <SecureImage
          key={`${uri}-${idx}`}
          uri={uri}
          resizeMode="cover"
          style={{
            width: itemWidth,
            height: itemHeight,
            borderRadius,
            marginRight: idx === images.length - 1 ? 0 : gap,
          }}
        />
      ));
    }, [borderRadius, gap, images, itemHeight, itemWidth]);

    if (!images.length) return null;

    return (
      <View style={{ width: '100%', overflow: 'hidden' }}>
        <Animated.View
          style={{
            flexDirection: 'row',
            transform: [{ translateX }],
          }}>
          {renderStrip()}
          <View style={{ width: gap }} />
          {renderStrip()}
        </Animated.View>
      </View>
    );
  },
);

interface Props {
  navigation: any;
}

const WelcomeScreen: React.FC<Props> = React.memo(({ navigation }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { width: windowWidth } = useWindowDimensions();

  const goRegister = useCallback(() => {
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('acq_welcome_cta_tapped', {
      ...ctx,
      cta: 'new_user',
    });
    navigation.replace('DemoStack');
  }, [navigation]);

  const goLogin = useCallback(() => {
    const ctx = buildAppAnalyticsContext();
    appAnalytics.track('acq_welcome_cta_tapped', {
      ...ctx,
      cta: 'existing_user',
    });
    navigation.navigate('OnboardingCarousel');
  }, [navigation]);

  const gap = s(12);
  const tileRadius = s(14);

  const itemWidth = useMemo(() => {
    const contentWidth = windowWidth - s(10) * 2;
    return Math.floor((contentWidth - gap * 3) / 4);
  }, [gap, windowWidth]);

  const itemHeight = useMemo(() => {
    const minHeight = v(250);
    const byWidth = Math.round(itemWidth);
    return Math.max(minHeight, byWidth);
  }, [itemWidth]);

  return (
    <SafeAreaWrapper
      edges={['top', 'left', 'right']}
      backgroundColor={theme.colors.surface}
      statusBarStyle="dark-content">
      <View style={styles.container}>
        <View style={styles.wallContainer}>
          <MarqueeRow
            images={WELCOME_WALL_IMAGES}
            direction="left"
            durationMs={24000}
            itemWidth={itemWidth}
            itemHeight={itemHeight}
            gap={gap}
            borderRadius={tileRadius}
          />
        </View>

        <View style={styles.copyContainer}>
          <Typography style={styles.headline}>
            Since{' '}
            <Typography
              style={{ color: theme.colors.primary, fontFamily: GTFonts.bold }}>
              2023
            </Typography>
            ,{'\n'}
            Thousands of business owners in 190+ countries trust us.
          </Typography>
        </View>

        <View style={styles.ctaContainer}>
          <Button
            backgroundColor={theme.colors.success}
            text={'New User'}
            disabled={false}
            icon={'user'}
            width={s(450)}
            fontSize={fs(14)}
            borderRadius={s(10)}
            height={v(48)}
            rippleColor="5d91c2"
            onPress={goRegister}
          />
          <View style={{ height: v(12) }} />
          <Button
            backgroundColor={theme.colors.primary}
            text={'Existing User'}
            disabled={false}
            icon={'lock'}
            width={s(450)}
            fontSize={fs(14)}
            borderRadius={s(10)}
            height={v(48)}
            rippleColor="5d91c2"
            onPress={goLogin}
          />

          <View style={styles.securityRow}>
            <FontAwesome6
              name="shield-halved"
              iconStyle="solid"
              size={iconSize(12)}
              color={theme.colors.primary}
            />
            <Typography style={styles.securityText}>
              Your data is 100% safe & secure. Trust assured
            </Typography>
          </View>
        </View>
      </View>
    </SafeAreaWrapper>
  );
});

const createStyles = (theme: any) => {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(10),
    },
    wallContainer: {
      paddingTop: v(28),
    },
    copyContainer: {
      flex: 1,
      justifyContent: 'flex-start',
      alignItems: 'center',
      paddingTop: v(36),
    },
    headline: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(isTablet() ? 32 : 26),
      lineHeight: fs(32),
      color: theme.colors.text,
      textAlign: 'center',
    },
    ctaContainer: {
      paddingBottom: v(isTablet() ? 18 : 24),
      alignItems: 'center',
    },
    securityRow: {
      marginTop: v(14),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(8),
    },
    securityText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.text,
      opacity: 0.75,
    },
  });
};

export default WelcomeScreen;
